/**
 * CanonicalNetworkContext - Context for canonical street network
 *
 * @version 1.0
 * @module core/spatial
 *
 * @description
 * Provides access to canonical network API and state management.
 * This is a basic implementation for Phase 1 - will be expanded in future phases.
 *
 * @remarks
 * Future phases will add:
 * - Network activation state
 * - Entity anchoring state
 * - Visualization state
 */

import { createContext, useContext, ReactNode } from 'react';
import { canonicalNetworkApi } from './services/canonical-network-api.service';
import type {
  CanonicalNode,
  CanonicalEdge,
  CreateCanonicalNodeDto,
  CreateCanonicalEdgeDto,
  QueryNetworkParams,
  NetworkStats,
} from './types';

/**
 * Context type
 */
export interface CanonicalNetworkContextType {
  // Node operations
  createNode: (projectId: string, data: CreateCanonicalNodeDto) => Promise<CanonicalNode>;
  queryNodes: (projectId: string, params?: QueryNetworkParams) => Promise<CanonicalNode[]>;
  getNode: (nodeId: string) => Promise<CanonicalNode>;
  deleteNode: (nodeId: string) => Promise<void>;

  // Edge operations
  createEdge: (projectId: string, data: CreateCanonicalEdgeDto) => Promise<CanonicalEdge>;
  queryEdges: (projectId: string, params?: QueryNetworkParams) => Promise<CanonicalEdge[]>;
  getEdge: (edgeId: string) => Promise<CanonicalEdge>;
  deleteEdge: (edgeId: string) => Promise<void>;

  // Statistics
  getNetworkStats: (projectId: string) => Promise<NetworkStats>;
}

/**
 * Context
 */
const CanonicalNetworkContext = createContext<CanonicalNetworkContextType | undefined>(
  undefined
);

/**
 * Provider props
 */
export interface CanonicalNetworkProviderProps {
  children: ReactNode;
}

/**
 * Provider component
 *
 * @remarks
 * Currently wraps the API client directly.
 * Future versions will add state management with React Query or Zustand.
 */
export function CanonicalNetworkProvider({ children }: CanonicalNetworkProviderProps) {
  const value: CanonicalNetworkContextType = {
    // Node operations - direct API pass-through
    createNode: canonicalNetworkApi.createNode,
    queryNodes: canonicalNetworkApi.queryNodes,
    getNode: canonicalNetworkApi.getNode,
    deleteNode: (nodeId: string) => canonicalNetworkApi.deleteNode(nodeId),

    // Edge operations - direct API pass-through
    createEdge: canonicalNetworkApi.createEdge,
    queryEdges: canonicalNetworkApi.queryEdges,
    getEdge: canonicalNetworkApi.getEdge,
    deleteEdge: (edgeId: string) => canonicalNetworkApi.deleteEdge('', edgeId),

    // Statistics
    getNetworkStats: canonicalNetworkApi.getNetworkStats,
  };

  return (
    <CanonicalNetworkContext.Provider value={value}>
      {children}
    </CanonicalNetworkContext.Provider>
  );
}

/**
 * Hook to use canonical network context
 *
 * @throws Error if used outside provider
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const { createNode, queryEdges } = useCanonicalNetwork();
 *
 *   const handleCreateNode = async () => {
 *     const node = await createNode(projectId, {
 *       geometry: { type: 'Point', coordinates: [-68.8458, -32.8895] },
 *       sourceType: 'manual'
 *     });
 *   };
 * }
 * ```
 */
export function useCanonicalNetwork(): CanonicalNetworkContextType {
  const context = useContext(CanonicalNetworkContext);
  if (!context) {
    throw new Error('useCanonicalNetwork must be used within CanonicalNetworkProvider');
  }
  return context;
}
